import React from "react";
import PropTypes from "prop-types";
import CsvdownloadGreyOutline from "@druva-apps/orion-icons/lib/ExportGreyOutline";

import Input from "../../input";
import Tooltip from "../../tooltip";
import Popover from "../../popover";
import Button from "../../button";
import { setExportableColumns, isTableBusy } from "../actions";
import { useTable } from "../context";
import exportToCsv from "../exportToCsv";
import { isString } from "../utils";

const toggleArrayItem = (arr, item) =>
  arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

const PopoverBody = ({ tableRef }) => {
  const {
    csvFileName,
    data,
    exportPopupMaxHeight,
    exportableColumnKeys,
    fireAction,
    onExportCSV,
    isLoading
  } = useTable();

  const columnManager = tableRef.current
    ? tableRef.current.getColumnManager()
    : null;

  const handleCSV = exportableColumnObjects => {
    fireAction(isTableBusy(true));

    setTimeout(() => {
      exportToCsv(
        exportableColumnObjects,
        data,
        csvFileName,
        onExportCSV,
        fireAction
      );
    }, 150);
  };

  const allColumns = columnManager
    ? columnManager._columns.filter(
        ({ key }) => key !== "__selection__" && key !== "__action__"
      )
    : [];

  const disabledColumnKeys = [];
  const enabledColumnKeys = [];

  allColumns.forEach(column => {
    if (typeof column.exportable === "undefined") {
      enabledColumnKeys.push(column.key);
    } else if (
      typeof column.exportable !== "undefined" &&
      column.exportable === false
    ) {
      disabledColumnKeys.push(column.key);
    }
  });

  const headerChecked =
    enabledColumnKeys.length === exportableColumnKeys.length;

  const exportableColumnObjects = allColumns.filter(col =>
    exportableColumnKeys.includes(col.key)
  );

  return (
    <div className="oui-d-flex oui-flex-column oui-justify-content-between">
      <div className="oui-p-2 oui-d-flex oui-border-bottom">
        <Input
          type="checkbox"
          label="Select All"
          checked={headerChecked}
          disabled={isLoading}
          onChange={e =>
            fireAction(
              setExportableColumns(
                e.target.checked
                  ? allColumns
                      .filter(c => !disabledColumnKeys.includes(c.key))
                      .map(c => c.key)
                  : []
              )
            )
          }
        />
      </div>
      <div
        className="oui-p-2 oui-overflow-auto"
        style={{ maxHeight: `${exportPopupMaxHeight}px`, overflow: "auto" }}
      >
        {allColumns.map((column, idx) => (
          <Input
            key={`export-column-${column.key}`}
            className={idx < allColumns.length - 1 ? "oui-mb-2" : ""}
            type="checkbox"
            label={
              isString(column.csvTitle)
                ? column.csvTitle
                : isString(column.title)
                ? column.title
                : ""
            }
            checked={exportableColumnKeys.includes(column.key)}
            disabled={disabledColumnKeys.includes(column.key) || isLoading}
            onChange={() =>
              fireAction(
                setExportableColumns(
                  toggleArrayItem(exportableColumnKeys, column.key)
                )
              )
            }
          />
        ))}
      </div>
      <div className="oui-p-2 oui-d-flex oui-justify-content-center oui-border-top">
        <Button
          label="Export To CSV"
          size="sm"
          disabled={exportableColumnKeys.length === 0 || isLoading}
          onClick={() => handleCSV(exportableColumnObjects)}
        />
      </div>
    </div>
  );
};

PopoverBody.propTypes = {
  tableRef: PropTypes.object
};

const ExportCsv = ({ tableRef }) => {
  return (
    <Popover
      size="auto"
      placement="bottom"
      type="uncontrolled"
      title="Select Exportable Columns"
      bodyClassName="oui-p-0"
      body={() => <PopoverBody tableRef={tableRef} />}
      isOpen
    >
      <Tooltip text="Export Data To CSV">
        <button
          type="button"
          style={{ borderRadius: "28px" }}
          aria-label="Export As CSV"
          className="orion-data-grid__navigation--empty-button oui-ml-3 data-grid-toolbar-button oui-w-auto oui-px-2"
        >
          <CsvdownloadGreyOutline clickable />

          <span
            className="oui-pl-2 oui-text-truncate"
            style={{ fontSize: "0.75rem" }}
          >
            Export to CSV
          </span>
        </button>
      </Tooltip>
    </Popover>
  );
};

ExportCsv.propTypes = {
  tableRef: PropTypes.object
};

export default ExportCsv;
